'use client';

import { useEffect, useMemo } from 'react';
import { Bundle, ServWithMods } from '@/util/types/serverTypes';
import { Button, Selection } from '@heroui/react';
import { FaArrowLeft } from 'react-icons/fa6';
import { useScheduler } from '../SchedulerContext';
import ServiceSearchActions from './ServiceSearchActions';
import ServiceConfirmation from './ServiceConfirmation';
import ServiceListbox from './ServiceListbox';

type SelectPrimaryServiceProps = {
  filteredServiceIds: string[];
  allServices: ServWithMods[];
  bundles: Bundle[];
  onNext: () => void;
  onStartOver?: () => void;
  onSearch?: () => void;
  onBack?: () => void;
  servicesFilteredList: ServWithMods[];
  setServicesFilteredList: (services: ServWithMods[]) => void;
};

export default function SelectPrimaryService({
  filteredServiceIds,
  allServices,
  bundles,
  onNext,
  onStartOver,
  onSearch,
  onBack,
  servicesFilteredList,
  setServicesFilteredList,
}: SelectPrimaryServiceProps) {
  const {
    selectedPrimaryService,
    setSelectedPrimaryService,
    companyStyles,
    scheduleSettings,
  } = useScheduler();

  const handleServiceSelect = (keys: Selection) => {
    if (keys === 'all') {
      setSelectedPrimaryService(null);
      return;
    }
    const selected = Array.from(keys as Set<string>)[0];
    setSelectedPrimaryService(selected || null);
  };

  // Filter services based on IDs; exclude add-on-only (cannot be primary) from primary step
  useEffect(() => {
    const filtered = allServices
      .filter((service) => filteredServiceIds.includes(service._id))
      .filter((service) => service.canBePrimary !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    setServicesFilteredList(filtered);
  }, [allServices, filteredServiceIds, setServicesFilteredList]);

  // Look the chosen service up in the full catalog rather than the
  // questionnaire's filtered list. A pre-filled scheduling link or a resumed
  // draft selects a primary service without ever running the filter, so
  // `servicesFilteredList` is empty in those flows — searching only there would
  // hide a perfectly valid selection behind an empty step.
  const selectedService = useMemo(
    () => allServices.find((s) => s._id === selectedPrimaryService),
    [allServices, selectedPrimaryService]
  );

  // Show confirmation message if a service is selected
  if (selectedPrimaryService && selectedService) {
    return (
      <ServiceConfirmation
        service={selectedService}
        onChangeSelection={() => setSelectedPrimaryService(null)}
        onNext={onNext}
        onBack={onBack}
        bundles={bundles}
        allServices={allServices}
        cameFromFilterForm={filteredServiceIds.length > 0}
      />
    );
  }

  // Show service selection list if no service is selected yet
  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <h3 className='text-xl font-bold'>Choose Your Primary Service</h3>
        <p className='text-gray-600'>
          Based on your selections, we found the following services:
        </p>
      </div>
      {servicesFilteredList.length > 0 ? (
        <div className='flex justify-center'>
          <ServiceListbox
            services={servicesFilteredList}
            selectedKeys={
              selectedPrimaryService
                ? new Set([selectedPrimaryService])
                : new Set()
            }
            onSelectionChange={handleServiceSelect}
            selectionMode='single'
            disallowEmptySelection
            ariaLabel='Primary Service Selection'
            className='max-w-2xl rounded-xl border bg-white p-4'
            iconBackgroundColor={
              companyStyles.companyColor
                ? `${companyStyles.companyColor}66`
                : undefined
            }
            iconTextColor={companyStyles.companyColor || undefined}
            showPrice={scheduleSettings?.showPricingInOnlineScheduler}
          />
        </div>
      ) : (
        <p className='text-center text-gray-500'>
          {filteredServiceIds.length > 0
            ? 'No primary services match your selections — the services found are add-on only. Search for an inspection below, or start the questions over.'
            : 'Search for the inspection you need below, or start the questions to narrow it down.'}
        </p>
      )}

      {/* Back Button - styled like question steps */}
      {onBack && (
        <div className='flex items-center justify-between pt-4'>
          <Button
            onPress={onBack}
            variant='flat'
            startContent={<FaArrowLeft />}
            color='default'
          >
            Back
          </Button>
        </div>
      )}

      {/* Can't find what you're looking for section. Rendered even when the list
          is empty — otherwise a flow that never ran the questionnaire (pre-filled
          link, resumed draft) has no way off this step at all. */}
      <ServiceSearchActions
        onStartOver={onStartOver || (() => setSelectedPrimaryService(null))}
        onSearch={onSearch}
      />
    </div>
  );
}
